import { DeleteTwoTone, EditTwoTone } from "@ant-design/icons";
import {
  Button,
  Col,
  DatePicker,
  Dropdown,
  Form,
  Input,
  Menu,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Tag,
  Typography,
} from "antd";
import ListTable from "common/ListTable";
import AppContent from "common/AppContent";
import AppPageHeader from "common/PageHeader";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useSelector } from "react-redux";

import {
  STOCK_LOT_CONFIGS_ADD_ENDPOINT,
  STOCK_LOT_CONFIGS_DELETE_ENDPOINT,
  STOCK_LOT_CONFIGS_EDIT_ENDPOINT,
  STOCK_LOT_CONFIGS_LIST_ENDPOINT,
} from "apiServices/API_ENDPOINTS";
import { deleteData, getData, postData, putData } from "apiServices/common";
import { useLocation } from "react-router";
import {
  STOCK_LOT_CREATE_PERMISSION,
  STOCK_LOT_DELETE_PERMISSION,
  STOCK_LOT_EDIT_PERMISSION,
} from "routes/permissions";
import { hasPermission, isArrayAndHasValue } from "utils/functions";
import ExtraContent from "../CommonComponents/ExtraContent";
import FilterComponent from "../CommonComponents/FilterComponent";

const StockLotConfigs = () => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [salesContracts, setSalesContracts] = useState([]);
  const [styles, setStyles] = useState([]);
  const [selectedSalesContract, setSelectedSalesContract] = useState(null);
  const [originalStyles, setOriginalStyles] = useState([]); // Track original styles for edit

  // Antd
  const [stockLotConfigForm] = Form.useForm();
  const { Title } = Typography;

  // Redux
  const filterData = useSelector(({ filter }) => filter.filterData);

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getAllStockLotConfigs = useCallback(
    async (filterValues) => {
      const { style_id, buyer_id, sales_contract_id, sub_contract_factory_id } =
        filterValues || {};
      setLoading(true);
      setFetchError(null);

      const query = `${STOCK_LOT_CONFIGS_LIST_ENDPOINT}?page=${currentPage}`;
      const bodyData = {
        per_page: 20,
        ...(style_id && { style_id }),
        ...(buyer_id && { buyer_id }),
        ...(sales_contract_id && { sales_contract_id }),
        ...(sub_contract_factory_id && { sub_contract_factory_id }),
      };

      const response = await getData(query, false, bodyData);
      if (response?.status === 200) {
        setLoading(false);
        setMetaData({
          defaultPageSize: response?.data?.data?.per_page,
          current: response?.data?.data?.current_page,
          defaultCurrent: response?.data?.data?.current_page,
          total: response?.data?.data?.total,
        });
        setTableData(response?.data?.data?.data);
      } else {
        setLoading(false);
        setFetchError("Could not load this list.");
        message.error("Error getting stock lot configs data");
      }
    },
    [currentPage],
  );

  // Load Sales Contracts
  const loadSalesContracts = async () => {
    try {
      const response = await getData("/api/sales_contract?per_page=100000");
      if (response?.status === 200) {
        setSalesContracts(response?.data?.data?.data || []);
        setStyles(response?.data?.data?.data?.style_details || []);
      }
    } catch (error) {
      message.error("Error loading sales contracts");
    }
  };

  // Effects
  useEffect(() => {
    getAllStockLotConfigs(filterData);
  }, [refresh, filterData, getAllStockLotConfigs]);

  useEffect(() => {
    loadSalesContracts();
  }, []);

  const onModalOpen = () => {
    setIsModalOpen(true);
  };

  const onCloseModal = () => {
    stockLotConfigForm.resetFields();
    setIsModalOpen(false);
    setIsEdit(false);
    setSelectedSalesContract(null);
    setStyles([]);
    setOriginalStyles([]);
  };

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const deleteSelectedStockLotConfig = async (id) => {
    setLoading(true);
    const response = await deleteData(
      `${STOCK_LOT_CONFIGS_DELETE_ENDPOINT}/${id}`,
    );

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Stock Lot Config Deleted Successfully");
      refetch();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  // Helper function to prepare styles for API
  const prepareStylesForAPI = (selectedStyleIds) => {
    if (isEdit) {
      // For edit mode, handle existing, new, and deleted styles
      const styles = [];

      // Add existing styles that are still selected
      originalStyles.forEach((originalStyle) => {
        if (selectedStyleIds.includes(originalStyle.style_id)) {
          styles.push({
            id: originalStyle.id,
            style_id: originalStyle.style_id,
            is_delete: 0,
          });
        } else {
          // Mark existing styles as deleted if they're not in selected list
          styles.push({
            id: originalStyle.id,
            style_id: originalStyle.style_id,
            is_delete: 1,
          });
        }
      });

      // Add new styles (those not in originalStyles)
      const originalStyleIds = originalStyles.map((style) => style.style_id);
      selectedStyleIds.forEach((styleId) => {
        if (!originalStyleIds.includes(styleId)) {
          styles.push({
            id: 0,
            style_id: styleId,
            is_delete: 0,
          });
        }
      });

      return styles;
    } else {
      // For create mode, all styles are new
      return selectedStyleIds.map((styleId) => ({
        id: 0,
        style_id: styleId,
        is_delete: 0,
      }));
    }
  };

  const editSelectedStockLotConfig = async (formValues) => {
    setLoading(true);

    const bodyData = {
      buyer_id: selectedSalesContract?.buyer_id || null,
      sub_contract_factory_id:
        selectedSalesContract?.sub_contract_factory_id || null,
      sales_contract_id: formValues?.sales_contract_id,
      create_date: formValues?.create_date
        ? dayjs(formValues.create_date).format("DD-MM-YYYY")
        : "",
      styles: prepareStylesForAPI(formValues?.styles || []),
    };

    const response = await putData(
      `${STOCK_LOT_CONFIGS_EDIT_ENDPOINT}/${formValues?.id}`,
      bodyData,
    );

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Stock Lot Config Edited Successfully");
      refetch();
      stockLotConfigForm.resetFields();
      onCloseModal();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const createNewStockLotConfig = async (formValues) => {
    setLoading(true);

    const bodyData = {
      buyer_id: selectedSalesContract?.buyer_id || null,
      sub_contract_factory_id:
        selectedSalesContract?.sub_contract_factory_id || null,
      sales_contract_id: formValues?.sales_contract_id,
      create_date: formValues?.create_date
        ? dayjs(formValues.create_date).format("DD-MM-YYYY")
        : "",
      styles: prepareStylesForAPI(formValues?.styles || []),
    };

    const response = await postData(STOCK_LOT_CONFIGS_ADD_ENDPOINT, bodyData);

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Stock Lot Config Created Successfully");
      refetch();
      stockLotConfigForm.resetFields();
      onCloseModal();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const openModalForEdit = (record) => {
    setIsEdit(true);

    const foundSalesContract = salesContracts?.find(
      (value) => value?.id === record?.sales_contract_id,
    );

    setSelectedSalesContract(record);
    setStyles(foundSalesContract?.style_details || []);

    // Store original styles for comparison during edit
    setOriginalStyles(record?.stock_lot_style_details || []);

    stockLotConfigForm.setFieldsValue({
      id: record?.id,
      sales_contract_id: record?.sales_contract_id,
      create_date: record?.create_date
        ? dayjs(record.create_date, "YYYY-MM-DD")
        : null,
      styles:
        record?.stock_lot_style_details?.map((style) => style.style_id) || [],
    });
    setIsModalOpen(true);
  };

  const handleSalesContractChange = (salesContractId) => {
    const selectedContract = salesContracts?.find(
      (contract) => contract.id === salesContractId,
    );
    if (selectedContract) {
      setSelectedSalesContract(selectedContract);
      setStyles(selectedContract?.style_details);
      stockLotConfigForm.setFieldsValue({
        styles: [],
      });
    }
  };

  const changePage = (newPage) => {
    setCurrentPage(newPage);
    refetch();
  };

  const tableColumns = [
    {
      title: "Sales Contract",
      dataIndex: ["sales_contract_info", "reference_no"],
      key: "sales_contract",
    },
    {
      title: "Buyer",
      dataIndex: ["buyer_info", "name"],
      key: "buyer",
    },
    {
      title: "Sub Contract Factory",
      dataIndex: ["sub_contract_factory_info", "name"],
      key: "sub_contract_factory",
      render: (text) => text || "N/A",
    },
    {
      title: "Create Date",
      dataIndex: "create_date",
      key: "create_date",
    },
    {
      title: "Styles",
      dataIndex: "stock_lot_style_details",
      key: "styles",
      render: (styles) => (
        <div>
          {styles?.map((style, index) => (
            <Tag key={index} color="blue" style={{ marginBottom: 4 }}>
              {style?.style_info?.style_no}
            </Tag>
          ))}
        </div>
      ),
    },
    {
      title: "Created By",
      dataIndex: ["created_by_info", "name"],
      key: "created_by",
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => {
        return (
          <Dropdown.Button
            overlay={
              <Menu
                items={[
                  {
                    label: (
                      <Popconfirm
                        title="Are you sure you want to delete?"
                        onConfirm={() => {
                          deleteSelectedStockLotConfig(record?.id);
                        }}
                      >
                        <span>
                          <DeleteTwoTone />
                        </span>
                        <span
                          style={{
                            color: "red",
                            marginLeft: 5,
                          }}
                        >
                          Delete
                        </span>
                      </Popconfirm>
                    ),
                    key: "1",
                    disabled: !hasPermission([STOCK_LOT_DELETE_PERMISSION]),
                  },
                  {
                    label: (
                      <>
                        <span>
                          <EditTwoTone />
                        </span>
                        <span
                          type="button"
                          style={{ marginLeft: 5 }}
                          onClick={() => {
                            openModalForEdit(record);
                          }}
                        >
                          Edit
                        </span>
                      </>
                    ),
                    key: "2",
                    disabled: !hasPermission([STOCK_LOT_EDIT_PERMISSION]),
                  },
                ]}
              />
            }
          ></Dropdown.Button>
        );
      },
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={getAllStockLotConfigs}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        filterName="stock-lot-configs"
      />
    ),
    [showFilter],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={onModalOpen}
                disabled={!hasPermission([STOCK_LOT_CREATE_PERMISSION])}
                addButtonText={
                  hasPermission([STOCK_LOT_CREATE_PERMISSION])
                    ? "Add New"
                    : "No Permission"
                }
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>Stock Lot Configs</Title>}
          />
        }
        content={
          <>
            {memoizedCustomFilter}
            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => getAllStockLotConfigs(filterData)}
                emptyText="No records to show yet."
                dataSource={tableData}
                columns={tableColumns}
                bordered
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize,
                  current: metaData?.current,
                  onChange: (newPage) => {
                    changePage(newPage);
                  },
                  defaultCurrent: metaData?.defaultCurrent,
                  total: metaData?.total,
                }}
              />
            </div>
          </>
        }
      />
      <Modal
        open={isModalOpen}
        title={isEdit ? "Edit Stock Lot Config" : "Create Stock Lot Config"}
        onCancel={() => {
          onCloseModal();
        }}
        maskStyle={{ backdropFilter: "blur(4px)" }}
        footer={null}
        width={800}
      >
        <Form
          form={stockLotConfigForm}
          layout="vertical"
          onFinish={
            isEdit ? editSelectedStockLotConfig : createNewStockLotConfig
          }
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="id" hidden />
              <Form.Item
                name="sales_contract_id"
                label="Sales Contract"
                rules={[
                  {
                    required: true,
                    message: "Sales Contract Required",
                  },
                ]}
              >
                <Select
                  placeholder="Select Sales Contract"
                  onChange={handleSalesContractChange}
                  showSearch
                  optionFilterProp="children"
                >
                  {isArrayAndHasValue(salesContracts) &&
                    salesContracts?.map((contract) => (
                      <Select.Option key={contract.id} value={contract.id}>
                        {contract.reference_no}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="create_date"
                label="Create Date"
                rules={[
                  {
                    required: true,
                    message: "Create Date Required",
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  placeholder="Select Date"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Buyer">
                <Input
                  value={selectedSalesContract?.buyer_info?.name || ""}
                  disabled
                  placeholder="Will be set automatically"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Sub Contract Factory">
                <Input
                  value={
                    selectedSalesContract?.sub_contract_factory_info?.name ||
                    "N/A"
                  }
                  disabled
                  placeholder="Will be set automatically"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="styles"
                label="Styles"
                rules={[
                  {
                    required: true,
                    message: "At least one style is required",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select Styles"
                  disabled={!selectedSalesContract}
                  showSearch
                  optionFilterProp="children"
                >
                  {styles?.map((style) => (
                    <Select.Option key={style?.style_id} value={style.style_id}>
                      {style?.style_info?.style_no} - {style.description}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Button
                type="danger"
                style={{ width: "100%" }}
                onClick={() => {
                  onCloseModal();
                }}
              >
                Discard
              </Button>
            </Col>
            <Col span={12}>
              <Button
                type="primary"
                htmlType="submit"
                style={{ width: "100%" }}
                loading={loading}
              >
                Save
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default StockLotConfigs;
